import React, { useState, useEffect, useRef, useMemo } from 'react';
import LayerPicker, { isLight } from './layer-picker';
import useGlobalState from '../../hooks/use-global-state';
import 'mapbox-gl/dist/mapbox-gl.css';
import LocationSearch from '../location-search';
import LocationMarker from './location-marker';
import { useLGA } from './hooks';
import DeckGL, { FlyToInterpolator } from 'deck.gl';
import { MapboxLayer } from '@deck.gl/mapbox';
import { StaticMap, NavigationControl, SVGOverlay } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import {
  selecSubmissionsAllFilters,
  setActiveSubmissions as _setActiveSubmissions,
} from '../../modules/app-duck';
import FrogPopup from './frog-popup';
import mapboxOverrides from './mapbox-overrides';
import getFrogIconLayer from './frog-icon-layer';
import WebMercatorViewport from 'viewport-mercator-project';
import { mobileMQ } from '../../modules/theme';
import { parse as SvgParser } from 'svg-parser';
import geojson2svg from 'geojson-to-svg';

const REACT_APP_MAPBOX_API_TOKEN = process.env.REACT_APP_MAPBOX_API_TOKEN;
const AUSTRALIA_BBOX = [
  [113.338953078, -43.6345972634],
  [153.569469029, -10.6681857235],
];

const getInitialViewport = (width, height) => ({
  latitude: -25,
  longitude: 134,
  zoom: 3.8,
  width: width,
  height: height,
  maxZoom: 13.38485969959837, // 300m
});

const Map = ({ width, height }) => {
  const { state, dispatch } = useGlobalState();
  const setActiveSubmissions = x => dispatch(_setActiveSubmissions(x));
  const mapHeight = height;
  // reference to initailviewport should be the same unless width or height changes
  const initial = useMemo(() => getInitialViewport(width, mapHeight), [
    mapHeight,
    width,
  ]);

  const [viewport, setViewport] = useState(initial);

  // Fit bounds to australia when initial changes (eg. width or height)
  useEffect(() => {
    setViewport(viewport => {
      const newViewport = new WebMercatorViewport(initial).fitBounds(
        AUSTRALIA_BBOX
      );
      return { ...initial, ...newViewport };
    });
  }, [initial]);

  const [hoveredFrog, setHoveredFrog] = useState(null);
  const [activeBaseLayerId, setActiveBaseLayerId] = useState(
    'cjynxhqlj350s1dqths5c9s5p'
  );

  useEffect(() => {
    if (!state.location) return;
    setViewport(viewport => ({
      ...viewport,
      latitude: state.location.value[1],
      longitude: state.location.value[0],
      zoom: initial.maxZoom,
      transitionInterpolator: new FlyToInterpolator(),
      transitionDuration: 500,
    }));
    return;
  }, [initial.maxZoom, state.location]);
  const lgaGeo = useLGA(setViewport);
  const mapboxRef = useRef();
  const deckRef = useRef();

  const handleMapLoad = () => {
    const map = mapboxRef.current.getMap();
    // Add scale control
    map.addControl(new mapboxgl.ScaleControl({ maxWidth: 80, unit: 'metric' }));

    // Figure out first label layer
    const layers = map.getStyle().layers;
    // Find the index of the first symbol layer in the map style
    let firstSymbolId;
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].type === 'symbol') {
        firstSymbolId = layers[i].id;
        break;
      }
    }
    // This adds the decklayer underneath the first labels/symbol layer
    map.addLayer(
      new MapboxLayer({ id: 'icon-layer', deck: deckRef.current.deck }),
      firstSymbolId
    );
  };

  const [gl, setGl] = useState();

  return (
    <div
      css={{
        width,
        height,
        position: 'relative',
        ...mapboxOverrides,
        '& canvas': {
          borderRadius: '4px',
        },
      }}
    >
      {state.loading && (
        <div
          css={{
            fontWeight: 'bold',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <img src={process.env.URL + '/loader.svg'} alt="loader" />
        </div>
      )}
      <DeckGL
        key={activeBaseLayerId}
        width={width}
        height={mapHeight}
        ref={deckRef}
        layers={[
          getFrogIconLayer({
            hoveredFrog,
            setHoveredFrog,
            zoom: viewport.zoom,
            deckRef,
            setActiveSubmissions,
            data: selecSubmissionsAllFilters(state),
          }),
        ]}
        viewState={viewport}
        onViewStateChange={x => {
          setViewport(x.viewState);
        }}
        pickingRadius={1}
        initialViewState={initial}
        controller={true}
        onClick={e => {
          const { x, y } = e;
          if (!e.layer) {
            setActiveSubmissions([]);
            return;
          }
          if (e.layer.id !== 'icon-layer') {
            setActiveSubmissions([]);
            return;
          }

          const nearby = deckRef.current.deck
            .pickMultipleObjects({
              x,
              y,
              // This is the amoun in pixels that we look for other circles
              // Could make it smarter and look at the current zoom level
              width: 10,
              height: 10,
              // layerIds: ['icon-layer'],
              depth: 100,
            })
            .map(({ object }) => object)
            .reduce((acc, val) => {
              if (acc.find(x => x.id === val.id)) {
                return acc;
              }
              return acc.concat(val);
            }, []);

          setActiveSubmissions(nearby);
        }}
        onWebGLInitialized={setGl}
        getCursor={({ isGrabbing }) =>
          hoveredFrog ? 'pointer' : isGrabbing ? 'grabbing' : 'grab'
        }
      >
        {gl && (
          <StaticMap
            gl={gl}
            width={width}
            height={height}
            onLoad={handleMapLoad}
            ref={mapboxRef}
            mapboxApiAccessToken={REACT_APP_MAPBOX_API_TOKEN}
            mapStyle={`https://api.mapbox.com/styles/v1/australian-museum/${activeBaseLayerId}?access_token=${process.env.REACT_APP_MAPBOX_API_TOKEN}`}
          >
            {/* This is somewhat hacky, had a bug with the sattelete map and geojson */}
            <SVGOverlay
              style={{
                zIndex: 1,
                pointerEvents: 'none',
              }}
              redraw={({ project }) => {
                if (!lgaGeo) return null;

                return lgaGeo.features.map((x, i) => {
                  const svgString = geojson2svg()
                    .projection(project)
                    .data(x)
                    .render();
                  const rootObj = SvgParser(svgString);
                  const svgObj = rootObj.children[0];
                  const gObj = svgObj.children[0];
                  const pathObjs = gObj.children;

                  return (
                    <g key={state.lga + i}>
                      {pathObjs.map((path, i) => {
                        return (
                          <path
                            key={i}
                            strokeWidth="2"
                            fill="none"
                            stroke={
                              isLight(activeBaseLayerId)
                                ? 'rgb(43, 117, 230)'
                                : 'rgb(255, 255, 255)'
                            }
                            d={path.properties.d}
                          />
                        );
                      })}
                    </g>
                  );
                });
              }}
            />
            <div
              css={{
                position: 'absolute',
                right: 10,
                top: 10,
                zIndex: 1,
              }}
            >
              <img
                css={{ marginBottom: 5, cursor: 'pointer' }}
                onClick={() =>
                  setViewport(v => {
                    const newV = new WebMercatorViewport({
                      ...initial,
                    }).fitBounds(AUSTRALIA_BBOX);
                    return {
                      ...v,
                      ...newV,
                      transitionInterpolator: new FlyToInterpolator(),
                      transitionDuration: 500,
                    };
                  })
                }
                tabIndex={0}
                role="button"
                alt="australia"
                src={process.env.URL + '/Australia.svg'}
              />
              <LayerPicker
                onLayerChange={setActiveBaseLayerId}
                activeBaseLayerId={activeBaseLayerId}
              />
            </div>
            {state.activeSubmissions.length > 0 && (
              <FrogPopup key={state.activeSubmissions[0].id} />
            )}
            <div
              css={{
                position: 'absolute',
                bottom: 30,
                right: 10,
                zIndex: 1,
                [mobileMQ]: {
                  bottom: 40,
                },
              }}
            >
              <NavigationControl
                onViewportChange={v => {
                  if (v.zoom >= initial.maxZoom) {
                    return;
                  }
                  setViewport({ ...v, maxZoom: initial.maxZoom });
                }}
                showCompass={false}
              />
            </div>
            <LocationMarker location={state.location} />

            <div
              css={{
                position: 'absolute',
                left: 10,
                top: 10,
                height: 38,
                maxWidth: 294,
                width: '50%',

                zIndex: 2,
              }}
            >
              <LocationSearch />
            </div>
          </StaticMap>
        )}
      </DeckGL>
    </div>
  );
};

export default Map;
